import { useState } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import MoneyInput from "@/components/admin/production/MoneyInput"

interface AssetFormState {
  label: string
  asset_kind: string
  purchase_cost_cents: number | ""
  purchased_on: string
  notes: string
}

interface AssetFormProps {
  initial?: Partial<AssetFormState>
  submitUrl: string
  method: "post" | "patch"
  errors?: Record<string, string[]>
  cancelUrl: string
}

export default function AssetForm({
  initial,
  submitUrl,
  method,
  errors,
  cancelUrl,
}: AssetFormProps) {
  const [state, setState] = useState<AssetFormState>({
    label: initial?.label ?? "",
    asset_kind: initial?.asset_kind ?? "",
    purchase_cost_cents: initial?.purchase_cost_cents ?? "",
    purchased_on: initial?.purchased_on ?? new Date().toISOString().slice(0, 10),
    notes: initial?.notes ?? "",
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload = { production_asset: state } as any
    if (method === "post") {
      router.post(submitUrl, payload)
    } else {
      router.patch(submitUrl, payload)
    }
  }

  const errorMessages = errors ? Object.entries(errors) : []

  return (
    <form onSubmit={submit} className="space-y-6 max-w-2xl">
      {errorMessages.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <ul className="list-disc pl-5 space-y-1">
            {errorMessages.map(([field, msgs]) => (
              <li key={field}>
                <strong>{field}</strong>: {(msgs as string[]).join(", ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <Label htmlFor="label">Label</Label>
        <Input
          id="label"
          value={state.label}
          onChange={(e) => setState({ ...state, label: e.target.value })}
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="asset_kind">Type</Label>
          <Input
            id="asset_kind"
            value={state.asset_kind}
            placeholder="screen / frame / dryer"
            onChange={(e) => setState({ ...state, asset_kind: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="purchase_cost_cents">Coût d&rsquo;achat (€)</Label>
          <MoneyInput
            id="purchase_cost_cents"
            cents={state.purchase_cost_cents}
            onChangeCents={(cents) =>
              setState({ ...state, purchase_cost_cents: cents })
            }
          />
        </div>
      </div>
      <div>
        <Label htmlFor="purchased_on">Date d&rsquo;achat</Label>
        <Input
          id="purchased_on"
          type="date"
          value={state.purchased_on}
          onChange={(e) => setState({ ...state, purchased_on: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={state.notes}
          onChange={(e) => setState({ ...state, notes: e.target.value })}
          rows={3}
        />
      </div>
      <div className="flex items-center gap-3">
        <Button type="submit">Enregistrer</Button>
        <a
          href={cancelUrl}
          className="text-sm text-gray-500 hover:text-gray-900"
        >
          Annuler
        </a>
      </div>
    </form>
  )
}
